/**
 * badgeFace — le fond et l'encre d'une chip, sans composant autour.
 *
 * Pur (aucun Unistyles) : `<Badge>` le peint, et `theme/contrast.test.ts` le mesure — ce que le
 * test lit est donc exactement ce que la chip pose à l'écran.
 */
import { inkOnFill, tintOnFill, type SemanticColorKey } from "../../theme/tokens/colors"

/** Semantic background variants. `neutral` uses a subtle surface with dark text. */
export type BadgeVariant =
    | "neutral"
    | "primary"
    | "accent"
    | "success"
    | "warning"
    | "warning-deep"
    | "danger"
    | "danger-deep"
    | "info"
    | "role-student"
    | "purple"

/**
 * La seule chose que la chip décide : quel APLAT porte chacune de ses variantes. « Neutre » est
 * la lavande ; les autres portent le nom de leur jeton.
 *
 * Les deux variantes `*-deep` sont les aplats FONCÉS : un statut qui doit s'imposer s'y écrit en
 * blanc et tient l'AA, là où l'orange plein ne porte le blanc qu'à 2,6 et le rouge plein qu'à
 * 4,0. Elles ne remplacent pas `warning` / `danger`, qui restent les chips d'information.
 *
 * Tout le reste — l'encre posée dessus, la tinte et l'encre de la tinte — appartient à l'aplat et
 * se lit sur son descripteur (`theme/tokens/colors` → `fills`) : les fonds clairs s'écrivent en
 * encre, les pleins en blanc, et « la chip jaune ne s'écrit qu'en marine ».
 *
 * `role-student` est la chip de gain de l'élève (« +25 XP ») : l'orange de son bouton, qui porte
 * le blanc à l'AA (4,6 ; le corail de l'univers, `role-student`, n'en tient que 3,3).
 * `tinted` (ce que demande `<GainChip>`) la pose en TINTE, comme le livre la dessine : rose pâle
 * à encre orange foncé en clair, prune à encre orange clair sur la nuit — les jetons
 * `role-student-tint` / `role-student-tint-ink`, que la nuit repointe. Pleine, elle se lisait
 * comme un second bouton.
 */
const VARIANT_FILL: Record<BadgeVariant, SemanticColorKey> = {
    neutral: "primary-soft",
    primary: "primary",
    accent: "accent",
    success: "success",
    warning: "warning",
    "warning-deep": "warning-deep",
    danger: "danger",
    "danger-deep": "danger-deep",
    info: "info",
    "role-student": "role-student-fill",
    /** Le mode Concentré d'un live (`<SoundModeBadge>`) : le violet, réservé aux surfaces LIVE. */
    purple: "purple",
}

/**
 * Le fond de la chip et l'encre de son texte et de son glyphe : l'aplat plein, ou sa tinte.
 *
 * `tinted` absent → la tinte pour `warning`, l'aplat plein sinon. L'orange plein ne porte aucune
 * encre de sa famille à l'AA (le blanc à 2,6, `warning-strong` à 2,3), et le marine qu'il
 * empruntait à la chip jaune tenait le calcul (5,1) mais se lisait bleu sur orange — illisible pour
 * Wajih (30/09). Une chip d'alerte d'information est donc une tinte ; un statut qui doit s'imposer
 * prend l'aplat foncé `warning-deep`.
 */
export function badgeFace(variant: BadgeVariant, tinted?: boolean): { bg: SemanticColorKey; ink: SemanticColorKey } {
    const fill = VARIANT_FILL[variant]
    if (!(tinted ?? variant === "warning")) return { bg: fill, ink: inkOnFill(fill) }
    const tint = tintOnFill(fill)
    return { bg: tint.on, ink: tint.ink }
}
